import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { INSTITUCION_SEMAR } from '../config/institutions';
import '../styles/global.css';

const PRESET_TOPICS = [
  {
    title: 'LOAM - Misión y Atribuciones de la Armada',
    text: `La Armada de México es una institución militar nacional de carácter permanente, cuya misión es emplear el poder naval de la Federación para la defensa exterior y coadyuvar en la seguridad interior del país. Entre sus atribuciones principales se encuentran: organizar, adiestrar, alistar, equipar y operar las fuerzas navales; proteger las vías generales de comunicación marítima, fluvial y lacustre; y salvaguardar la vida humana en el mar mediante operaciones de búsqueda y rescate.`
  },
  {
    title: 'Código de Conducta SEMAR',
    text: `Los Servidores Públicos de la Secretaría de Marina deben actuar con rectitud, honradez, patriotismo y disciplina. El honor es la cualidad moral que nos impulsa a cumplir con el deber sin vacilaciones. La disciplina es la norma a que los militares deben sujetar su conducta, teniendo como base la obediencia y un alto sentido del honor, de la justicia y del deber.`
  },
  {
    title: 'Nomenclatura del Buque',
    text: `El buque es una construcción flotante destinada a navegar. Se divide en tres partes principales: Proa o parte delantera, Popa o parte trasera, y Centro. La obra viva o carena es la parte sumergida del casco por debajo de la línea de flotación. La obra muerta es la parte del casco emergida por encima de la línea de flotación. La eslora es la longitud total del buque.`
  },
  {
    title: 'Jerarquía Naval',
    text: `La jerarquía en la Armada de México comprende la siguiente escala de grados: Almirantes (Almirante, Vicealmirante, Contralmirante), Capitanes (Capitán de Navío, Capitán de Fragata, Capitán de Corbeta), Oficiales (Teniente de Navío, Teniente de Fragata, Teniente de Corbeta), Clases (Primer Maestre, Segundo Maestre, Tercer Maestre, Cabo) y Marinería (Marinero).`
  }
];

const VozVortex = () => {
  const { institucion, profileLoading } = useAuth();
  const isSemar = institucion === INSTITUCION_SEMAR;
  const [inputText, setInputText] = useState('');
  const [bookTitle, setBookTitle] = useState('');
  const [voices, setVoices] = useState([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState('');
  const [rate, setRate] = useState(1.0);
  const [pitch, setPitch] = useState(1.0);
  const [volume, setVolume] = useState(1.0);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentParagraphIdx, setCurrentParagraphIdx] = useState(0);
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  
  const [savedBooks, setSavedBooks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const textContainerRef = useRef(null);
  const seededPresetRef = useRef(false);

  // Los temas precargados son material de SEMAR; en otras instituciones el editor inicia vacío.
  useEffect(() => {
    if (profileLoading || seededPresetRef.current) return;
    seededPresetRef.current = true;
    if (isSemar) {
      setBookTitle(PRESET_TOPICS[0].title);
      setInputText(PRESET_TOPICS[0].text);
    }
  }, [profileLoading, isSemar]);

  // Dividir texto en párrafos
  const paragraphs = inputText.split(/\n\n+/).filter(p => p.trim().length > 0);

  // Cargar voces del navegador y filtrar estrictamente en español
  useEffect(() => {
    const updateVoices = () => {
      if ('speechSynthesis' in window) {
        const availVoices = window.speechSynthesis.getVoices();
        
        // Filtrar ÚNICAMENTE voces en español
        const spanishOnly = availVoices.filter(v => {
          const lang = (v.lang || '').toLowerCase();
          return lang.startsWith('es') || lang.includes('es-') || lang.includes('_es');
        });

        // Ordenar dando prioridad a voces Neural, Natural, Google, Microsoft, Premium, Enhanced
        spanishOnly.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          const isAHighQuality = nameA.includes('natural') || nameA.includes('neural') || nameA.includes('google') || nameA.includes('online') || nameA.includes('premium') || nameA.includes('enhanced');
          const isBHighQuality = nameB.includes('natural') || nameB.includes('neural') || nameB.includes('google') || nameB.includes('online') || nameB.includes('premium') || nameB.includes('enhanced');
          
          if (isAHighQuality && !isBHighQuality) return -1;
          if (!isAHighQuality && isBHighQuality) return 1;
          return a.name.localeCompare(b.name);
        });

        setVoices(spanishOnly);

        // Seleccionar por defecto la mejor voz mexicana o hispana disponible
        if (spanishOnly.length > 0 && !selectedVoiceURI) {
          const defaultVoice = spanishOnly.find(v => (v.lang || '').toLowerCase().includes('mx')) ||
                             spanishOnly.find(v => (v.lang || '').toLowerCase().includes('419')) ||
                             spanishOnly[0];
          if (defaultVoice) {
            setSelectedVoiceURI(defaultVoice.voiceURI);
          }
        }
      }
    };

    updateVoices();
    if ('speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = updateVoices;
    }

    // Cargar audiolibros guardados
    const stored = localStorage.getItem('vortex_saved_audiobooks');
    if (stored) {
      try { setSavedBooks(JSON.parse(stored)); } catch (e) { console.error(e); }
    }

    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Control de reproducción
  const speakParagraph = (idx) => {
    if (!('speechSynthesis' in window)) {
      alert('Tu navegador no soporta la API de Síntesis de Voz.');
      return;
    }

    window.speechSynthesis.cancel();

    if (idx < 0 || idx >= paragraphs.length) {
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentParagraphIdx(0);
      setCurrentCharIndex(0);
      return;
    }

    const textToSpeak = paragraphs[idx];
    const utterance = new SpeechSynthesisUtterance(textToSpeak);

    const voice = voices.find(v => v.voiceURI === selectedVoiceURI);
    if (voice) utterance.voice = voice;
    utterance.rate = rate;
    utterance.pitch = pitch;
    utterance.volume = volume;

    utterance.onboundary = (event) => {
      if (event.name === 'word') {
        setCurrentCharIndex(event.charIndex);
      }
    };

    utterance.onend = () => {
      if (idx + 1 < paragraphs.length) {
        setCurrentParagraphIdx(idx + 1);
        setCurrentCharIndex(0);
        speakParagraph(idx + 1);
      } else {
        setIsPlaying(false);
        setIsPaused(false);
        setCurrentParagraphIdx(0);
        setCurrentCharIndex(0);
        if (isRecording) stopRecording();
      }
    };

    utterance.onerror = (e) => {
      console.error('Speech error:', e);
      setIsPlaying(false);
      setIsPaused(false);
    };

    setCurrentParagraphIdx(idx);
    setIsPlaying(true);
    setIsPaused(false);
    window.speechSynthesis.speak(utterance);
  };

  const handlePlay = () => {
    if (isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
      setIsPlaying(true);
    } else {
      speakParagraph(currentParagraphIdx);
    }
  };

  const handlePause = () => {
    if (isPlaying) {
      window.speechSynthesis.pause();
      setIsPaused(true);
      setIsPlaying(false);
    }
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setCurrentParagraphIdx(0);
    setCurrentCharIndex(0);
    if (isRecording) stopRecording();
  };

  const handleNext = () => {
    if (currentParagraphIdx + 1 < paragraphs.length) {
      speakParagraph(currentParagraphIdx + 1);
    }
  };

  const handlePrev = () => {
    if (currentParagraphIdx > 0) {
      speakParagraph(currentParagraphIdx - 1);
    }
  };

  // Grabación de Audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(audioBlob);
        setRecordedAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      if (!isPlaying) handlePlay();
    } catch (err) {
      alert('Para grabar el audio narrado, otorga permiso de micrófono/audio.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Guardar audiolibro localmente
  const saveAudiobookLocally = () => {
    if (!inputText.trim()) return;
    const newBook = {
      id: Date.now(),
      title: bookTitle || 'Audiolibro Sin Título',
      text: inputText,
      date: new Date().toLocaleDateString(),
      voice: voices.find(v => v.voiceURI === selectedVoiceURI)?.name || 'Voz Estándar',
      rate
    };

    const updated = [newBook, ...savedBooks.filter(b => b.title !== newBook.title)];
    setSavedBooks(updated);
    localStorage.setItem('vortex_saved_audiobooks', JSON.stringify(updated));
    alert('📚 ¡Audiolibro guardado exitosamente en tu biblioteca local!');
  };

  const loadSavedBook = (book) => {
    handleStop();
    setBookTitle(book.title);
    setInputText(book.text);
  };

  const deleteSavedBook = (id) => {
    const updated = savedBooks.filter(b => b.id !== id);
    setSavedBooks(updated);
    localStorage.setItem('vortex_saved_audiobooks', JSON.stringify(updated));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        setInputText(content);
        setBookTitle(file.name.replace(/\.[^/.]+$/, ""));
      }
    };
    reader.readAsText(file);
  };

  // Helper para resaltar palabras en vivo
  const renderHighlightedParagraph = (paraText, pIdx) => {
    if (pIdx !== currentParagraphIdx || (!isPlaying && !isPaused)) {
      return paraText;
    }

    const words = paraText.split(' ');
    let runningCharCount = 0;

    return words.map((word, wIdx) => {
      const isCurrent = runningCharCount <= currentCharIndex && currentCharIndex < runningCharCount + word.length + 1;
      runningCharCount += word.length + 1;

      return (
        <span
          key={wIdx}
          style={{
            background: isCurrent ? 'rgba(197, 160, 89, 0.45)' : 'transparent',
            color: isCurrent ? '#fff' : 'inherit',
            borderRadius: isCurrent ? '4px' : '0',
            padding: isCurrent ? '1px 3px' : '0',
            fontWeight: isCurrent ? 'bold' : 'normal',
            transition: 'background 0.1s ease'
          }}
        >
          {word}{' '}
        </span>
      );
    });
  };

  // Clasificar voces en español por región
  const voicesMexico = voices.filter(v => (v.lang || '').toLowerCase().includes('mx'));
  const voicesLatam = voices.filter(v => {
    const l = (v.lang || '').toLowerCase();
    return (l.includes('419') || l.includes('ar') || l.includes('co') || l.includes('cl') || l.includes('pe') || l.includes('us') || l.includes('ve') || l.includes('gt') || l.includes('cr') || l.includes('pr')) && !l.includes('mx');
  });
  const voicesEspana = voices.filter(v => (v.lang || '').toLowerCase().includes('es-es') || (v.lang || '').toLowerCase() === 'es_es');
  const voicesOtherEs = voices.filter(v => !voicesMexico.includes(v) && !voicesLatam.includes(v) && !voicesEspana.includes(v));

  const formatVoiceName = (v) => {
    const nameLower = v.name.toLowerCase();
    const isNatural = nameLower.includes('natural') || nameLower.includes('neural') || nameLower.includes('google') || nameLower.includes('online') || nameLower.includes('premium') || nameLower.includes('enhanced');
    return `${isNatural ? '⭐ ' : ''}${v.name} (${v.lang})`;
  };

  return (
      <div style={{ height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto', display: 'flex', flexDirection: 'column', padding: '10px 0 40px' }}>
      {/* Encabezado Táctico */}
      <div style={{
        background: 'linear-gradient(135deg, #0d2035 0%, #0a1a2a 100%)',
        border: '1px solid rgba(197,160,89,0.3)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        display: 'flex',
        justifyQuery: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ fontSize: '1.8rem', filter: 'drop-shadow(0 0 10px rgba(197,160,89,0.5))' }}>🎙️</span>
            <h1 style={{
              margin: 0,
              fontFamily: "'Orbitron', sans-serif",
              fontSize: '1.4rem',
              color: '#c5a059',
              letterSpacing: '2px',
              textTransform: 'uppercase'
            }}>
              VOZ VORTEX · ESTUDIO AUDIBLE
            </h1>
            <span style={{
              background: 'rgba(52, 211, 153, 0.15)',
              color: '#34d399',
              border: '1px solid #34d399',
              borderRadius: '20px',
              padding: '2px 10px',
              fontSize: '0.65rem',
              fontWeight: 'bold',
              letterSpacing: '1px'
            }}>
              100% GRATIS & OFFLINE
            </span>
          </div>
          <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>
            Convierte tus lecturas y temas de estudio en audiolibros narrados en tiempo real con sintaxis de voz del navegador.
          </p>
        </div>

        {/* Botones de acción rápida */}
        <div style={{ display: 'flex', gap: '10px' }}>
          <label style={{
            background: 'rgba(197, 160, 89, 0.15)',
            border: '1px solid var(--gold-matte)',
            color: 'var(--gold-light)',
            padding: '8px 14px',
            borderRadius: '8px',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            📁 Cargar Archivo .TXT
            <input type="file" accept=".txt,.md" onChange={handleFileUpload} style={{ display: 'none' }} />
          </label>
          <button
            onClick={saveAudiobookLocally}
            style={{
              background: 'rgba(52, 211, 153, 0.2)',
              border: '1px solid #10b981',
              color: '#6ee7b7',
              padding: '8px 14px',
              borderRadius: '8px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            💾 Guardar Audiolibro
          </button>
        </div>
      </div>

      {/* Temas Preconfigurados de Estudio (solo SEMAR) */}
      {isSemar && (
        <div style={{ marginBottom: '20px' }}>
          <span style={{
            fontFamily: "'Orbitron', sans-serif",
            fontSize: '0.75rem',
            color: '#c5a059',
            letterSpacing: '1px',
            display: 'block',
            marginBottom: '8px'
          }}>
            ⚡ SELECCIONAR TEMA DE ESTUDIO RÁPIDO:
          </span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {PRESET_TOPICS.map((topic, i) => (
              <button
                key={i}
                onClick={() => {
                  handleStop();
                  setBookTitle(topic.title);
                  setInputText(topic.text);
                }}
                style={{
                  background: bookTitle === topic.title ? 'rgba(197, 160, 89, 0.25)' : 'rgba(15, 25, 35, 0.7)',
                  border: `1px solid ${bookTitle === topic.title ? '#c5a059' : '#1e293b'}`,
                  color: bookTitle === topic.title ? '#fff' : '#cbd5e1',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s ease, color 0.2s ease, background-color 0.2s ease'
                }}
              >
                📖 {topic.title}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: '20px', alignItems: 'start' }}>
        
        {/* Panel Izquierdo: Editor y Reproductor de Texto */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Título del Libro */}
          <input
            type="text"
            value={bookTitle}
            onChange={(e) => setBookTitle(e.target.value)}
            placeholder="Título del Audiolibro..."
            style={{
              background: '#0a1628',
              border: '1px solid #2a3a5c',
              borderRadius: '10px',
              padding: '12px 16px',
              color: '#fff',
              fontSize: '1rem',
              fontWeight: 'bold',
              outline: 'none',
              fontFamily: "'Orbitron', sans-serif"
            }}
          />

          {/* Barra de Controles de Reproducción */}
          <div style={{
            background: 'linear-gradient(180deg, #102136 0%, #0a1727 100%)',
            border: '1px solid rgba(197, 160, 89, 0.3)',
            borderRadius: '12px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              
              {/* Botones Play / Pause / Stop / Prev / Next */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={handlePrev}
                  disabled={currentParagraphIdx === 0}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid #2a3a5c',
                    color: '#fff',
                    borderRadius: '8px',
                    width: '38px',
                    height: '38px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                  title="Párrafo Anterior"
                >
                  ⏮️
                </button>

                {!isPlaying ? (
                  <button
                    onClick={handlePlay}
                    style={{
                      background: 'linear-gradient(135deg, #c5a059 0%, #9e7d3b 100%)',
                      border: 'none',
                      color: '#071526',
                      borderRadius: '8px',
                      padding: '0 20px',
                      height: '38px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      boxShadow: '0 0 12px rgba(197, 160, 89, 0.4)'
                    }}
                  >
                    ▶️ REPRODUCIR
                  </button>
                ) : (
                  <button
                    onClick={handlePause}
                    style={{
                      background: '#eab308',
                      border: 'none',
                      color: '#000',
                      borderRadius: '8px',
                      padding: '0 20px',
                      height: '38px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    ⏸️ PAUSAR
                  </button>
                )}

                <button
                  onClick={handleStop}
                  style={{
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid #ef4444',
                    color: '#f87171',
                    borderRadius: '8px',
                    padding: '0 14px',
                    height: '38px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.85rem'
                  }}
                  title="Detener"
                >
                  ⏹️ DETENER
                </button>

                <button
                  onClick={handleNext}
                  disabled={currentParagraphIdx >= paragraphs.length - 1}
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid #2a3a5c',
                    color: '#fff',
                    borderRadius: '8px',
                    width: '38px',
                    height: '38px',
                    cursor: 'pointer',
                    fontSize: '1rem'
                  }}
                  title="Párrafo Siguiente"
                >
                  ⏭️
                </button>
              </div>

              {/* Botón Grabador de Audio MP3 */}
              <div>
                {!isRecording ? (
                  <button
                    onClick={startRecording}
                    style={{
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid #ef4444',
                      color: '#f87171',
                      borderRadius: '8px',
                      padding: '8px 14px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    🔴 Grabador de Voz MP3
                  </button>
                ) : (
                  <button
                    onClick={stopRecording}
                    style={{
                      background: '#ef4444',
                      border: 'none',
                      color: '#fff',
                      borderRadius: '8px',
                      padding: '8px 14px',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      animation: 'pulse 1s infinite'
                    }}
                  >
                    ⏹️ Detener Grabación
                  </button>
                )}
              </div>
            </div>

            {/* Progreso de Lectura */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                Párrafo {currentParagraphIdx + 1} de {paragraphs.length || 1}
              </span>
              <div style={{ flex: 1, height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  width: `${((currentParagraphIdx + 1) / (paragraphs.length || 1)) * 100}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #c5a059, #34d399)',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>

            {recordedAudioUrl && (
              <div style={{ background: 'rgba(16, 185, 129, 0.1)', padding: '10px', borderRadius: '8px', border: '1px solid #10b981', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '0.8rem', color: '#a7f3d0' }}>🎉 Grabación lista:</span>
                <audio controls src={recordedAudioUrl} style={{ height: '32px', flex: 1 }} />
                <a href={recordedAudioUrl} download={`${bookTitle || 'audiolibro'}.webm`} style={{ color: '#34d399', fontSize: '0.8rem', fontWeight: 'bold' }}>
                  📥 Descargar
                </a>
              </div>
            )}
          </div>

          {/* Area de Texto / Lectura Sincronizada */}
          <div style={{
            background: '#071322',
            border: '1px solid #1e293b',
            borderRadius: '12px',
            padding: '20px',
            minHeight: '260px',
            maxHeight: '400px',
            overflowY: 'auto'
          }} ref={textContainerRef}>
            {isPlaying || isPaused ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', lineHeight: '1.7', fontSize: '1rem', color: '#e2e8f0' }}>
                {paragraphs.map((para, idx) => (
                  <p
                    key={idx}
                    onClick={() => speakParagraph(idx)}
                    style={{
                      margin: 0,
                      padding: '8px 12px',
                      borderRadius: '8px',
                      background: idx === currentParagraphIdx ? 'rgba(197, 160, 89, 0.12)' : 'transparent',
                      borderLeft: idx === currentParagraphIdx ? '4px solid #c5a059' : '4px solid transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease, border-left-color 0.2s ease'
                    }}
                  >
                    {renderHighlightedParagraph(para, idx)}
                  </p>
                ))}
              </div>
            ) : (
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Pega aquí el libro, temas de estudio o leyes que deseas convertir a audiolibro..."
                style={{
                  width: '100%',
                  height: '240px',
                  background: 'transparent',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            )}
          </div>
        </div>

        {/* Panel Derecha: Configuración de Voz y Biblioteca Local */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          
          {/* Controles de Voz */}
          <div style={{
            background: 'linear-gradient(180deg, #0d2035 0%, #081626 100%)',
            border: '1px solid rgba(197, 160, 89, 0.25)',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <h3 style={{
              margin: '0 0 14px 0',
              fontFamily: "'Orbitron', sans-serif",
              fontSize: '0.85rem',
              color: '#c5a059',
              letterSpacing: '1px'
            }}>
              ⚙️ AJUSTES DE SÍNTESIS
            </h3>

            {/* Selector de Voz */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '6px' }}>
                Voz Narradora:
              </label>
              <select
                value={selectedVoiceURI}
                onChange={(e) => setSelectedVoiceURI(e.target.value)}
                style={{
                  width: '100%',
                  background: '#0a1628',
                  color: '#fff',
                  border: '1px solid #2a3a5c',
                  borderRadius: '8px',
                  padding: '8px 10px',
                  fontSize: '0.8rem',
                  outline: 'none'
                }}
              >
                {voicesMexico.length > 0 && (
                  <optgroup label="🇲🇽 México (es-MX)">
                    {voicesMexico.map(v => (
                      <option key={v.voiceURI} value={v.voiceURI}>
                        {formatVoiceName(v)}
                      </option>
                    ))}
                  </optgroup>
                )}

                {voicesLatam.length > 0 && (
                  <optgroup label="🌎 Latinoamérica & EE.UU.">
                    {voicesLatam.map(v => (
                      <option key={v.voiceURI} value={v.voiceURI}>
                        {formatVoiceName(v)}
                      </option>
                    ))}
                  </optgroup>
                )}

                {voicesEspana.length > 0 && (
                  <optgroup label="🇪🇸 España / Castellano (es-ES)">
                    {voicesEspana.map(v => (
                      <option key={v.voiceURI} value={v.voiceURI}>
                        {formatVoiceName(v)}
                      </option>
                    ))}
                  </optgroup>
                )}

                {voicesOtherEs.length > 0 && (
                  <optgroup label="🌐 Otras Voces en Español">
                    {voicesOtherEs.map(v => (
                      <option key={v.voiceURI} value={v.voiceURI}>
                        {formatVoiceName(v)}
                      </option>
                    ))}
                  </optgroup>
                )}

                {voices.length === 0 && (
                  <option value="">Cargando voces en español del sistema...</option>
                )}
              </select>
            </div>

            {/* Velocidad / Rate */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>
                <span>Velocidad de Lectura:</span>
                <span style={{ color: '#c5a059', fontWeight: 'bold' }}>{rate}x</span>
              </div>
              <input
                type="range"
                min="0.5"
                max="2.0"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#c5a059' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: '#64748b' }}>
                <span>0.5x (Lento)</span>
                <span>1.0x (Normal)</span>
                <span>2.0x (Rápido)</span>
              </div>
            </div>

            {/* Tono / Pitch */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>
                <span>Tono de Voz:</span>
                <span style={{ color: '#c5a059', fontWeight: 'bold' }}>{pitch}</span>
              </div>
              <input
                type="range"
                min="0.5"
                max="1.5"
                step="0.1"
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#c5a059' }}
              />
            </div>

            {/* Volumen */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8', marginBottom: '4px' }}>
                <span>Volumen:</span>
                <span style={{ color: '#c5a059', fontWeight: 'bold' }}>{Math.round(volume * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                style={{ width: '100%', accentColor: '#c5a059' }}
              />
            </div>

            <div style={{ marginTop: '14px', padding: '10px', background: 'rgba(197, 160, 89, 0.08)', borderRadius: '8px', border: '1px solid rgba(197, 160, 89, 0.2)', fontSize: '0.72rem', color: '#cbd5e1', lineHeight: '1.4' }}>
              💡 <strong style={{ color: '#c5a059' }}>Tip de Naturalidad:</strong> Las voces con <strong style={{ color: '#ffd700' }}>⭐ (Google / Natural / Neural)</strong> son las que ofrecen una narración más humana y fluida.
            </div>
          </div>

          {/* Biblioteca "Mis Audiolibros Guardados" */}
          <div style={{
            background: 'linear-gradient(180deg, #0d2035 0%, #081626 100%)',
            border: '1px solid #1e293b',
            borderRadius: '12px',
            padding: '16px'
          }}>
            <h3 style={{
              margin: '0 0 12px 0',
              fontFamily: "'Orbitron', sans-serif",
              fontSize: '0.85rem',
              color: '#c5a059',
              letterSpacing: '1px'
            }}>
              📚 MIS AUDIOLIBROS ({savedBooks.length})
            </h3>

            {savedBooks.length === 0 ? (
              <p style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center', margin: '10px 0' }}>
                No tienes audiolibros guardados localmente. ¡Guarda tus temas favoritos arriba!
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                {savedBooks.map(b => (
                  <div
                    key={b.id}
                    style={{
                      padding: '8px 10px',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} role="button" tabIndex={0} onClick={() => loadSavedBook(b)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadSavedBook(b); } }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {b.title}
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#64748b' }}>
                        {b.date} · {b.rate}x
                      </div>
                    </div>
                    <button
                      onClick={() => deleteSavedBook(b.id)}
                      style={{ background: 'transparent', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: '0.8rem' }}
                      title="Eliminar"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </div>
    </div>
  );
};

export default VozVortex;
